import React, { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { Alert, Box } from '@mui/material';

import useCurrentUser from '~/hooks/useCurrentUser';

import { dateNow, isAfter, isBefore, subDays } from '~/services/TimeService';
import UsersService from '~/services/UsersService';

/**
 * Extracts the first name from a full name string
 */
function getFirstName(fullName: string | null | undefined): string {
  if (!fullName) {
    return '';
  }
  return fullName.split(' ')[0] || '';
}

export default function SmsVerifyWarning() {
  const { user } = useCurrentUser();

  // Fetch current user's warning level
  const { data: warningData } = useQuery({
    queryKey: ['smsVerificationWarning', user?.id_member],
    queryFn: async () => {
      if (!user?.id_member) {
        return null;
      }
      return await UsersService.getSmsVerificationWarning(user.id_member);
    },
    enabled: !!user?.id_member,
  });

  // Fetch SMS verification stats
  const { data: smsStatsData } = useQuery({
    queryKey: ['smsVerificationStats'],
    queryFn: async ({ signal }) => {
      return await UsersService.getSmsVerificationStats(signal ? { signal } : undefined);
    },
  });

  // Calculate SMS verification count for past 7 days for current user
  const smsCountPast7Days = useMemo(() => {
    if (!smsStatsData?.result?.dailyCounts || !user?.id_member) {
      return 0;
    }

    // Find the current user's data
    const userStats = smsStatsData.result.dailyCounts.find((agent) => agent.agentId === user.id_member);

    if (!userStats) {
      return 0;
    }

    // Get today's date and calculate 7 days ago
    const today = dateNow();
    const sevenDaysAgo = subDays(7, today);

    // Sum counts for the past 7 days
    let count = 0;
    for (const dailyCount of userStats.dailyCounts) {
      const dateStr = dailyCount.date;
      // Parse YYYYMMDD format
      const year = parseInt(dateStr.substring(0, 4), 10);
      const month = parseInt(dateStr.substring(4, 6), 10) - 1;
      const day = parseInt(dateStr.substring(6, 8), 10);
      const date = new Date(year, month, day);

      if (!isBefore(date, sevenDaysAgo) && !isAfter(date, today)) {
        count += dailyCount.count;
      }
    }

    return count;
  }, [smsStatsData, user?.id_member]);

  // Early return after all hooks have been called
  if (!user?.id_member) {
    return null;
  }

  const firstName = getFirstName(user.fullName);
  const warningLevel = warningData?.result?.warningType;

  if (warningLevel === 'soft') {
    return (
      <Box mt={2} mb={2}>
        <Alert severity="warning" variant="outlined">
          Hi {firstName ? `${firstName},` : ''} <br />
          We're now tracking stats on SMS verification per agent, and agents not regularly verifying via SMS will see
          this reminder. <br />
          You've verified by SMS{' '}
          <strong>
            {smsCountPast7Days} {smsCountPast7Days === 1 ? 'time' : 'times'} in the past 7 days.
          </strong>
          <br />
          SMS is the <strong>most secure</strong> way to verify, and <strong>required whenever possible</strong>. <br />
          After SMS, no other verification step is required. (No need to confirm name, booking number, email, hotel,
          etc, unless things looks suspicious or fraudulent)
          <br />
          <br />
          On board, and don't want to see this anymore? Ping Koren Sela and Joshua Paling on Teams to turn it off for
          you.
        </Alert>
      </Box>
    );
  }

  if (warningLevel === 'hard') {
    return (
      <Box mt={2} mb={2}>
        <Alert severity="error" variant="filled">
          <br />
          <br />
          Hi {firstName ? `${firstName},` : ''} <br />
          We're now tracking stats on SMS verification per agent, and agents not verifying via SMS will see this
          reminder. <br />
          You've verified by SMS{' '}
          <strong>
            {smsCountPast7Days} {smsCountPast7Days === 1 ? 'time' : 'times'} in the past 7 days.
          </strong>
          <br />
          SMS is the <strong>most secure</strong> way to verify, and <strong>required whenever possible</strong>. <br />
          After SMS, no other verification step is required. (No need to confirm name, booking number, email, hotel,
          etc, unless things looks suspicious or fraudulent)
          <br />
          <br />
          You should only get this big red alert, if someone notices you have not responded to the previous alert, and
          are still not verifying via SMS. <br />
          Someone has manually set that you personally should see this hard warning. It is not done automatically.
          <br />
          <br />
          It's intentionally big, annoying, and impossible to miss.
          <br />
          <br />
          Don't want to see this anymore? <br />
          Please start verifying via SMS.
          <br />
          Then, ping Koren Sela and Joshua Paling on Teams to turn it off for you.
          <br />
          <br />
          Thanks for helping keep Luxury Escapes safe and secure!
          <br />
          <br />
        </Alert>
      </Box>
    );
  }

  return null;
}
